// src/pages/finance/FinanceDashboard.jsx
import React, { useMemo, useState } from "react";
import Layout from "../../components/common/Layout";
import { getNavItems, getPanelLabel } from "../../utils/navigation";
import { useAuth } from "../../context/AuthContext";
import { useCollection, useAllUsers } from "../../hooks/useFirestore";

/* ── Helpers ──────────────────────────────────────────────────────── */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function toDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === "function") return ts.toDate();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtDate(ts) {
  const d = toDate(ts);
  if (!d) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function inr(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

function isPendingReimbursement(status) {
  return status === "PENDING_FINANCE" || status === "PENDING";
}

/* ── Custom SVGs matching the mockup icons ───────────────────────── */

const ICONS = {
  // Indian Rupee in circle
  payroll: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ width: 28, height: 28 }}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 7.5h7M8.5 10.5h5M8.5 7.5v6.5M13.5 10.5a2.2 2.2 0 0 1-2.2 2.2H8.5l4.5 4.8" />
    </svg>
  ),
  // Clock outline
  pendingReimb: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ width: 28, height: 28 }}>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 15" />
    </svg>
  ),
  // Checkmark
  approvedReimb: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 26, height: 26 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  // Cross
  rejectedReimb: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 24, height: 24 }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  // Banknote / Payouts icon
  payouts: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ width: 28, height: 28 }}>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" />
    </svg>
  ),
  // Shield with checkmark
  pf: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" style={{ width: 28, height: 28 }}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  ),
};

function KpiCard({ label, value, sub, icon, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "#ffffff",
        borderRadius: "14px",
        border: "1px solid #E2E8F0",
        padding: "1.2rem 1.25rem",
        display: "flex",
        alignItems: "center",
        gap: "1.15rem",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s ease",
        minHeight: "102px",
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.06)";
          e.currentTarget.style.borderColor = "#CBD5E1";
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.03)";
          e.currentTarget.style.borderColor = "#E2E8F0";
        }
      }}
    >
      <div
        style={{
          width: "44px",
          height: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "#1B4B31",
        }}
      >
        {icon}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
        <span style={{ fontSize: "1.55rem", fontWeight: 800, color: "#111827", lineHeight: 1.15 }}>
          {value}
        </span>
        <span style={{ fontSize: "0.92rem", fontWeight: 600, color: "#374151" }}>
          {label}
        </span>
        <span style={{ fontSize: "0.78rem", color: "#6B7280" }}>
          {sub}
        </span>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    APPROVED: { bg: "#DEF7EC", color: "#03543F" },
    Paid: { bg: "#DEF7EC", color: "#03543F" },
    REJECTED: { bg: "#FDE8E8", color: "#9B1C1C" },
    PENDING_FINANCE: { bg: "#FEF08A", color: "#713F12" },
    PENDING: { bg: "#FEF08A", color: "#713F12" },
    "Pending Payment": { bg: "#FEF08A", color: "#713F12" },
  };
  const s = map[status] || { bg: "#F3F4F6", color: "#374151" };
  const label = status === "PENDING_FINANCE" ? "Pending" : status;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontSize: "0.75rem",
        fontWeight: 600,
        padding: "0.2rem 0.6rem",
        borderRadius: "999px",
        background: s.bg,
        color: s.color,
      }}
    >
      {label}
    </span>
  );
}

/* ── Main component ──────────────────────────────────────────────── */

export default function FinanceDashboard() {
  const { userProfile } = useAuth();
  const navItems = getNavItems(userProfile);
  const panelLabel = getPanelLabel(userProfile);

  const { data: reimbursements, loading: rLoad } = useCollection("reimbursements");
  const { data: pendingPayments, loading: pLoad } = useCollection("pendingPayments");
  const { data: users, loading: uLoad } = useAllUsers();

  const { currentMonthName, currentMonthIndex, currentYear, todayStr } = useMemo(() => {
    const d = new Date();
    return {
      currentMonthName: MONTHS[d.getMonth()],
      currentMonthIndex: d.getMonth(),
      currentYear: d.getFullYear(),
      todayStr: d.toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    };
  }, []);

  const employees = useMemo(() => users.filter((u) => u.isActive !== false), [users]);

  const scopedPayments = useMemo(
    () =>
      pendingPayments.filter(
        (p) =>
          p.month?.toLowerCase() === currentMonthName.toLowerCase() &&
          Number(p.year) === currentYear
      ),
    [pendingPayments, currentMonthName, currentYear]
  );

  const paidThisMonth = useMemo(
    () => scopedPayments.filter((p) => p.status === "Paid"),
    [scopedPayments]
  );
  const pendingPayouts = useMemo(
    () =>
      scopedPayments.filter(
        (p) => p.status === "Pending Payment" || p.status === "Pending" || !p.status
      ),
    [scopedPayments]
  );
  const totalPayrollCost = useMemo(
    () => scopedPayments.reduce((a, p) => a + Number(p.gross || p.net || 0), 0),
    [scopedPayments]
  );
  const totalPF = useMemo(
    () => scopedPayments.reduce((a, p) => a + Number(p.pf || 0), 0),
    [scopedPayments]
  );

  const pendingReimbursements = useMemo(
    () => reimbursements.filter((c) => isPendingReimbursement(c.status)),
    [reimbursements]
  );
  const approvedThisMonth = useMemo(
    () =>
      reimbursements.filter((c) => {
        if (c.status !== "APPROVED") return false;
        const d = toDate(c.reviewedAt || c.submittedAt || c.createdAt);
        return !d || (d.getMonth() === currentMonthIndex && d.getFullYear() === currentYear);
      }),
    [reimbursements, currentMonthIndex, currentYear]
  );
  const rejectedThisMonth = useMemo(
    () =>
      reimbursements.filter((c) => {
        if (c.status !== "REJECTED") return false;
        const d = toDate(c.reviewedAt || c.submittedAt || c.createdAt);
        return !d || (d.getMonth() === currentMonthIndex && d.getFullYear() === currentYear);
      }),
    [reimbursements, currentMonthIndex, currentYear]
  );

  const approvedAmountThisMonth = useMemo(
    () => approvedThisMonth.reduce((a, c) => a + Number(c.grandTotal || 0), 0),
    [approvedThisMonth]
  );

  // Expense category breakdown, from approved reimbursements only
  const expenseBreakdown = useMemo(() => {
    const approved = reimbursements.filter((c) => c.status === "APPROVED");
    return {
      Transport: approved.reduce((a, c) => a + Number(c.transportTotal || c.transport || 0), 0),
      Accommodation: approved.reduce((a, c) => a + Number(c.accomTotal || c.accommodation || 0), 0),
      Meals: approved.reduce((a, c) => a + Number(c.mealsTotal || c.meals || 0), 0),
      Other: approved.reduce((a, c) => a + Number(c.otherTotal || c.other || 0), 0),
    };
  }, [reimbursements]);

  const expenseMax = Math.max(1, ...Object.values(expenseBreakdown));

  // Recent transactions: merge paid payroll + approved reimbursements, most recent first
  const recentTransactions = useMemo(() => {
    const payrollTx = pendingPayments
      .filter((p) => p.status === "Paid")
      .map((p) => ({
        id: `payroll-${p.id}`,
        type: "Payroll",
        name: p.employeeName || p.employeeId || "—",
        amount: Number(p.net || 0),
        date: p.paidAt,
        sortMs: toMillis(p.paidAt),
      }));
    const reimbTx = reimbursements
      .filter((c) => c.status === "APPROVED")
      .map((c) => ({
        id: `reimb-${c.id}`,
        type: "Reimbursement",
        name: c.emp?.name || "—",
        amount: Number(c.grandTotal || 0),
        date: c.reviewedAt || c.submittedAt,
        sortMs: toMillis(c.reviewedAt || c.submittedAt),
      }));
    return [...payrollTx, ...reimbTx].sort((a, b) => b.sortMs - a.sortMs).slice(0, 8);
  }, [pendingPayments, reimbursements]);

  // Pending approvals list (reimbursements awaiting finance review), most recent first
  const pendingApprovalsList = useMemo(
    () =>
      [...pendingReimbursements]
        .sort((a, b) => toMillis(b.submittedAt) - toMillis(a.submittedAt))
        .slice(0, 6),
    [pendingReimbursements]
  );

  /* ── KPI popup state ─────────────────────────────────────────────── */
  const [popup, setPopup] = useState(null); // { title, rows, kind }

  function openPopup(title, rows, kind) {
    setPopup({ title, rows, kind });
  }

  const loading = rLoad || pLoad || uLoad;
  const userName = userProfile?.displayName || userProfile?.name || "Radha";

  return (
    <Layout navItems={navItems} panelLabel={panelLabel}>
      {/* ── Page Header ─────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          gap: "0.75rem",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "1.75rem",
              fontWeight: 800,
              color: "#1B4B31",
              margin: 0,
              letterSpacing: "-0.02em",
            }}
          >
            Finance Dashboard
          </h1>
          <p style={{ fontSize: "0.875rem", color: "#4B5563", margin: "4px 0 0 0" }}>
            {todayStr} · Manage payroll, expenses and business travel.
          </p>
        </div>
        <div style={{ fontSize: "0.92rem", fontWeight: 500, color: "#374151", alignSelf: "center" }}>
          <span>Welcome, {userName.split(" ")[0]}</span>
        </div>
      </div>

      {/* ── KPI Cards (4 cols on top row, 2 cols on second row) ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "1.25rem",
          marginBottom: "1.75rem",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "1.25rem",
            gridColumn: "1 / -1",
          }}
          className="finance-kpi-container"
        >
          {/* Card 1: Payroll Cost */}
          <KpiCard
            label="Payroll Cost"
            value={loading ? "—" : inr(totalPayrollCost)}
            sub={`${currentMonthName} ${currentYear}`}
            icon={ICONS.payroll}
            onClick={() => openPopup("Payroll Cost — " + currentMonthName, scopedPayments, "payroll")}
          />

          {/* Card 2: Pending Reimbursements */}
          <KpiCard
            label="Pending Reimbursements"
            value={loading ? "—" : pendingReimbursements.length}
            sub="Awaiting your review"
            icon={ICONS.pendingReimb}
            onClick={() => openPopup("Pending Reimbursements", pendingReimbursements, "reimb")}
          />

          {/* Card 3: Approved This Month */}
          <KpiCard
            label="Approved This Month"
            value={loading ? "—" : inr(approvedAmountThisMonth)}
            sub={`${approvedThisMonth.length} claim(s)`}
            icon={ICONS.approvedReimb}
            onClick={() => openPopup("Approved This Month", approvedThisMonth, "reimb")}
          />

          {/* Card 4: Rejected This Month */}
          <KpiCard
            label="Rejected This Month"
            value={loading ? "—" : rejectedThisMonth.length}
            sub="Claims rejected"
            icon={ICONS.rejectedReimb}
            onClick={() => openPopup("Rejected This Month", rejectedThisMonth, "reimb")}
          />

          {/* Card 5: Pending Payouts */}
          <KpiCard
            label="Pending Payouts"
            value={loading ? "—" : pendingPayouts.length}
            sub="Payroll awaiting payment"
            icon={ICONS.payouts}
            onClick={() => openPopup("Pending Payouts", pendingPayouts, "payroll")}
          />

          {/* Card 6: PF Contributions */}
          <KpiCard
            label="PF Contributions"
            value={loading ? "—" : inr(totalPF)}
            sub={`${currentMonthName} ${currentYear}`}
            icon={ICONS.pf}
            onClick={() => openPopup("PF Contributions — " + currentMonthName, scopedPayments, "payroll")}
          />
        </div>
      </div>

      {/* ── 3-Column Content Row ────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "1.25rem",
          marginBottom: "1.75rem",
        }}
        className="finance-summary-grid"
      >
        {/* Card 1: Payroll Summary */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            border: "1px solid #E2E8F0",
            padding: "1.25rem 1.4rem",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.25rem",
            }}
          >
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "#1B4B31" }}>
              Payroll Summary
            </h3>
            <span
              style={{
                fontSize: "0.78rem",
                fontWeight: 600,
                padding: "3px 10px",
                background: "#E8F0EC",
                color: "#1B4B31",
                borderRadius: "999px",
              }}
            >
              {currentMonthName} {currentYear}
            </span>
          </div>

          {loading ? (
            <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0", fontSize: "0.88rem" }}>
              Loading…
            </div>
          ) : scopedPayments.length === 0 ? (
            <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0", fontSize: "0.88rem" }}>
              No payroll generated for this period yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#1F2937" }}>Paid</div>
                  <div style={{ fontSize: "0.8rem", color: "#6B7280" }}>{paidThisMonth.length} employee(s)</div>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "4px 14px",
                    borderRadius: "999px",
                  }}
                >
                  {inr(paidThisMonth.reduce((a, p) => a + Number(p.net || 0), 0))}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#1F2937" }}>Pending Payment</div>
                  <div style={{ fontSize: "0.8rem", color: "#6B7280" }}>{pendingPayouts.length} employee(s)</div>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "4px 14px",
                    borderRadius: "999px",
                  }}
                >
                  {inr(pendingPayouts.reduce((a, p) => a + Number(p.net || 0), 0))}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#1F2937" }}>Total Gross</div>
                  <div style={{ fontSize: "0.8rem", color: "#6B7280" }}>All payroll rows this period</div>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "4px 14px",
                    borderRadius: "999px",
                  }}
                >
                  {inr(totalPayrollCost)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Card 2: Expense Breakdown */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            border: "1px solid #E2E8F0",
            padding: "1.25rem 1.4rem",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.25rem",
            }}
          >
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "#1B4B31" }}>
              Expense Breakdown
            </h3>
            <span
              style={{
                fontSize: "0.78rem",
                fontWeight: 600,
                padding: "3px 10px",
                background: "#E8F0EC",
                color: "#1B4B31",
                borderRadius: "999px",
              }}
            >
              Approved claims
            </span>
          </div>

          {loading ? (
            <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0", fontSize: "0.88rem" }}>
              Loading…
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              {Object.entries(expenseBreakdown).map(([key, value]) => (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.75rem",
                  }}
                >
                  <span
                    style={{
                      minWidth: "110px",
                      fontSize: "0.88rem",
                      color: "#374151",
                      fontWeight: 500,
                    }}
                  >
                    {key}
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: "8px",
                      background: "#F3F4F6",
                      borderRadius: "999px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(1, (value / expenseMax) * 100)}%`,
                        height: "100%",
                        background: "#1B4B31",
                        borderRadius: "999px",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      minWidth: "68px",
                      textAlign: "right",
                      fontSize: "0.88rem",
                      fontWeight: 700,
                      color: "#111827",
                    }}
                  >
                    {inr(value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Card 3: Travel & Accommodation */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            border: "1px solid #E2E8F0",
            padding: "1.25rem 1.4rem",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.25rem",
            }}
          >
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "#1B4B31" }}>
              Travel &amp; Accommodation
            </h3>
            <span
              style={{
                fontSize: "0.78rem",
                fontWeight: 600,
                padding: "3px 10px",
                background: "#E8F0EC",
                color: "#1B4B31",
                borderRadius: "999px",
              }}
            >
              Approved claims
            </span>
          </div>

          {loading ? (
            <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0", fontSize: "0.88rem" }}>
              Loading…
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#1F2937" }}>Transport</div>
                  <div style={{ fontSize: "0.8rem", color: "#6B7280" }}>Total across approved claims</div>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "4px 14px",
                    borderRadius: "999px",
                  }}
                >
                  {inr(expenseBreakdown.Transport)}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#1F2937" }}>Accommodation</div>
                  <div style={{ fontSize: "0.8rem", color: "#6B7280" }}>Total across approved claims</div>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "4px 14px",
                    borderRadius: "999px",
                  }}
                >
                  {inr(expenseBreakdown.Accommodation)}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#1F2937" }}>Combined Travel Spend</div>
                  <div style={{ fontSize: "0.8rem", color: "#6B7280" }}>Transport + accommodation</div>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "4px 14px",
                    borderRadius: "999px",
                  }}
                >
                  {inr(expenseBreakdown.Transport + expenseBreakdown.Accommodation)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Additional Activity / Analytics Cards ───────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "1.25rem",
        }}
        className="finance-summary-grid"
      >
        {/* Pending Approvals */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            border: "1px solid #E2E8F0",
            padding: "1.25rem 1.4rem",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "#1B4B31" }}>
              Pending Approvals
            </h3>
            <span
              style={{
                fontSize: "0.78rem",
                fontWeight: 600,
                padding: "3px 10px",
                background: "#FEF08A",
                color: "#713F12",
                borderRadius: "999px",
              }}
            >
              {pendingReimbursements.length} pending
            </span>
          </div>

          {loading ? (
            <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0", fontSize: "0.88rem" }}>
              Loading…
            </div>
          ) : pendingApprovalsList.length === 0 ? (
            <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0", fontSize: "0.88rem" }}>
              Nothing waiting on you right now.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {pendingApprovalsList.map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.6rem 0.75rem",
                    background: "#F9FAFB",
                    borderRadius: "8px",
                    border: "1px solid #F3F4F6",
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#EAB308",
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#111827" }}>
                      {c.emp?.name || "—"} · {inr(c.grandTotal)}
                    </div>
                    <div style={{ fontSize: "0.76rem", color: "#6B7280" }}>
                      {c.emp?.dept || "—"} · submitted {fmtDate(c.submittedAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            border: "1px solid #E2E8F0",
            padding: "1.25rem 1.4rem",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "#1B4B31" }}>
              Recent Transactions
            </h3>
          </div>

          {loading ? (
            <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0", fontSize: "0.88rem" }}>
              Loading…
            </div>
          ) : recentTransactions.length === 0 ? (
            <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0", fontSize: "0.88rem" }}>
              No transactions yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {recentTransactions.slice(0, 4).map((tx) => (
                <div
                  key={tx.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.6rem 0.75rem",
                    background: "#F9FAFB",
                    borderRadius: "8px",
                    border: "1px solid #F3F4F6",
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#10B981",
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#111827" }}>
                      {tx.name} · {inr(tx.amount)}
                    </div>
                    <div style={{ fontSize: "0.76rem", color: "#6B7280" }}>
                      {tx.type} · {fmtDate(tx.date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Workforce Snapshot */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "14px",
            border: "1px solid #E2E8F0",
            padding: "1.25rem 1.4rem",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700, color: "#1B4B31" }}>
              Workforce Snapshot
            </h3>
            <span
              style={{
                fontSize: "0.78rem",
                fontWeight: 600,
                padding: "3px 10px",
                background: "#DEF7EC",
                color: "#03543F",
                borderRadius: "999px",
              }}
            >
              {employees.length} active
            </span>
          </div>

          {loading ? (
            <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0", fontSize: "0.88rem" }}>
              Loading…
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#1F2937" }}>Active Employees</div>
                  <div style={{ fontSize: "0.8rem", color: "#6B7280" }}>Eligible for payroll</div>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    color: "#111827",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "4px 14px",
                    borderRadius: "999px",
                  }}
                >
                  {employees.length}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#1F2937" }}>Paid This Month</div>
                  <div style={{ fontSize: "0.8rem", color: "#6B7280" }}>Out of active employees</div>
                </div>
                <div
                  style={{
                    background: "#DEF7EC",
                    color: "#03543F",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "4px 14px",
                    borderRadius: "999px",
                  }}
                >
                  {paidThisMonth.length}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "0.92rem", fontWeight: 600, color: "#1F2937" }}>Not Yet Paid</div>
                  <div style={{ fontSize: "0.8rem", color: "#6B7280" }}>Pending payroll this month</div>
                </div>
                <div
                  style={{
                    background: "#FEF08A",
                    color: "#713F12",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    padding: "4px 14px",
                    borderRadius: "999px",
                  }}
                >
                  {Math.max(0, employees.length - paidThisMonth.length)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── KPI Popup Modal ─────────────────────────────────── */}
      {popup && (
        <div
          onClick={() => setPopup(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 700,
              maxHeight: "80vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 16,
              padding: "1.2rem",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "1.2rem", color: "#1B4B31" }}>{popup.title}</h2>
              <button
                onClick={() => setPopup(null)}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "1.4rem",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            {popup.rows.length === 0 ? (
              <div style={{ color: "#9CA3AF", textAlign: "center", padding: "1.5rem 0" }}>
                No records found.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {popup.rows.map((row) => {
                  const isPayroll = popup.kind === "payroll";
                  const name = isPayroll
                    ? row.employeeName || row.employeeId
                    : row.emp?.name || "—";
                  const sub = isPayroll
                    ? `${row.month} ${row.year}`
                    : `${row.emp?.dept || "—"} · submitted ${fmtDate(row.submittedAt)}`;
                  const amount = isPayroll ? row.net : row.grandTotal;
                  const status = isPayroll
                    ? row.status
                    : row.status === "PENDING"
                    ? "PENDING_FINANCE"
                    : row.status;
                  return (
                    <div
                      key={row.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0.8rem",
                        border: "1px solid #e5e7eb",
                        borderRadius: 12,
                        background: "#f9fafb",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                        <div
                          style={{
                            width: 42,
                            height: 42,
                            borderRadius: "50%",
                            background: "#1B4B31",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                          }}
                        >
                          {(name || "??").slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: "#111827" }}>{name}</div>
                          <div style={{ fontSize: "0.82rem", color: "#6b7280" }}>{sub}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 600, color: "#374151" }}>{inr(amount)}</div>
                        <div style={{ marginTop: 4 }}>
                          <StatusPill status={status} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Responsive Grid Media Query styles */}
      <style>{`
        @media (max-width: 1200px) {
          .finance-kpi-container {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .finance-summary-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 640px) {
          .finance-kpi-container {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </Layout>
  );
}